/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { AfterViewInit, ComponentRef, Directive, DoCheck, ElementRef, Inject, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ScrollbarComponent } from './scrollbar.component';
import { AppendComponentService } from '../../services/append-component.service';

@Directive({
  selector: '[wfmScrollbar]'
})
export class ScrollbarDirective implements AfterViewInit, DoCheck, OnDestroy {
  private verticalScroll: ComponentRef<ScrollbarComponent> = undefined;
  private horizontalScroll: ComponentRef<ScrollbarComponent> = undefined;

  /*
   * flags to indicate if vertical and horizontal scrolls are present
   */
  private verticalPresent = false;
  private horizontalPresent = false;

  /*
   * flags to indicate if mouse is far from vertical and horizontal edges
   */
  private isFarFromVerticalEdge = true;
  private isFarFromHorizontalEdge = true;

  /*
   * flag to indicate if scrollbar should be visible
   */
  private visible = false;

  private rtl = false;

  private rect = {
    offsetTop: 0,
    offsetHeight: 0,
    scrollHeight: 0,
    offsetLeft: 0,
    offsetWidth: 0,
    scrollWidth: 0
  };

  private window: Window;

  private get $element() {
    return this.element.nativeElement as HTMLElement;
  }

  private get $parent() {
    return this.$element.parentElement;
  }

  constructor(
    @Inject(DOCUMENT) private document: any,
    private element: ElementRef,
    private appendComponent: AppendComponentService
  ) {
    this.window = (this.document as Document).defaultView;
  }

  checkTimeout = 0;

  ngDoCheck(): void {
    if (this.checkTimeout) return;

    this.checkTimeout = this.window.setTimeout(() => {
      // horizontal scroll
      if (this.horizontalPresent !== this.$element.scrollWidth > this.$element.offsetWidth) {
        this.horizontalPresent = this.$element.scrollWidth > this.$element.offsetWidth;
        // Add/Remove horizontal scroll
        if (this.horizontalPresent) {
          this.horizontalScroll = this.horizontalScroll ? this.horizontalScroll : this.addScrollbar('horizontal');
        } else {
          this.horizontalScroll = this.removeScrollbar(this.horizontalScroll);
        }
      }

      // vertiocal scroll
      if (this.verticalPresent !== this.$element.scrollHeight > this.$element.offsetHeight) {
        this.verticalPresent = this.$element.scrollHeight > this.$element.offsetHeight;
        // Add/Remove vertical scroll
        if (this.verticalPresent) {
          this.verticalScroll = this.verticalScroll ? this.verticalScroll : this.addScrollbar('vertical');
        } else {
          this.verticalScroll = this.removeScrollbar(this.verticalScroll);
        }
      }

      const rtl: boolean =
        this.window.getComputedStyle(this.$element).getPropertyValue('direction').toLocaleLowerCase() === 'rtl';

      if (
        this.$element.scrollWidth !== this.rect.scrollWidth ||
        this.$element.offsetWidth !== this.rect.offsetWidth ||
        this.$element.scrollHeight !== this.rect.scrollHeight ||
        this.$element.offsetHeight !== this.rect.offsetHeight ||
        this.$element.offsetTop !== this.rect.offsetTop ||
        this.$element.offsetLeft !== this.rect.offsetLeft ||
        this.rtl !== rtl
      ) {
        // update horizontal dimentions
        this.rtl = rtl;
        this.updateRect();
      }

      this.checkTimeout = 0;
    }, 100);
  }

  ngAfterViewInit(): void {
    this.$element.addEventListener('scroll', this.scrollHandler);
    this.$element.addEventListener('mousemove', this.mousemoveHandler);
    this.$element.addEventListener('mouseenter', this.mouseenterHandler);
    this.$element.addEventListener('mouseleave', this.mouseleaveHandler);

    const position = this.window.getComputedStyle(this.$parent).getPropertyValue('position').toLocaleLowerCase();
    if (position === 'static') {
      this.$parent.style.position = 'relative';
    }
    this.$element.classList.add('no-scroll-scrollable');
  }

  ngOnDestroy(): void {
    this.element?.nativeElement.removeEventListener('scroll', this.scrollHandler);
    this.element?.nativeElement.removeEventListener('mousemove', this.mousemoveHandler);
    this.element?.nativeElement.removeEventListener('mouseenter', this.mouseenterHandler);
    this.element?.nativeElement.removeEventListener('mouseleave', this.mouseleaveHandler);
  }

  private mouseenterHandler = () => this.changeVisibility(true);
  private mouseleaveHandler = () => this.changeVisibility(false);

  private changeVisibility(visible: boolean) {
    this.visible = visible;
    if (this.verticalScroll && this.verticalScroll.instance && !this.verticalScroll.instance.isChanging) {
      this.verticalScroll.instance.hidden = this.isFarFromVerticalEdge && !visible;
      this.verticalScroll.instance.detectChanges();
    }
    if (this.horizontalScroll && this.horizontalScroll.instance && !this.horizontalScroll.instance.isChanging) {
      this.horizontalScroll.instance.hidden = this.isFarFromHorizontalEdge && !visible;
      this.horizontalScroll.instance.detectChanges();
    }
  }

  private mousemoveHandler = ($event: MouseEvent) => {
    this.ngDoCheck();

    if (this.verticalScroll && !this.verticalScroll.instance.isChanging) {
      this.isFarFromVerticalEdge =
        (this.rtl && $event.offsetX > 20) || (!this.rtl && $event.offsetX < this.$element.clientWidth - 20);
      this.verticalScroll.instance.hidden = this.isFarFromVerticalEdge && !this.visible;
      this.verticalScroll.instance.small = this.isFarFromVerticalEdge;
      this.verticalScroll.instance.detectChanges();
    }

    if (this.horizontalScroll && !this.horizontalScroll.instance.isChanging) {
      this.isFarFromHorizontalEdge = $event.offsetY < this.$element.clientHeight - 20;
      this.horizontalScroll.instance.hidden = this.isFarFromHorizontalEdge && !this.visible;
      this.horizontalScroll.instance.small = this.isFarFromHorizontalEdge;
      this.horizontalScroll.instance.detectChanges();
    }
  };

  private scrollHandler = () => {
    this.ngDoCheck();

    if (this.verticalScroll && this.verticalScroll.instance) {
      this.verticalScroll.instance.position = this.$element.scrollTop;
      this.verticalScroll.instance.detectChanges();
    }

    if (this.horizontalScroll && this.horizontalScroll.instance) {
      this.horizontalScroll.instance.position = this.$element.scrollLeft * (this.rtl ? -1 : 1);
      this.horizontalScroll.instance.detectChanges();
    }
  };

  private updateRect() {
    this.rect.scrollWidth = this.$element.scrollWidth;
    this.rect.offsetWidth = this.$element.offsetWidth;
    this.rect.scrollHeight = this.$element.scrollHeight;
    this.rect.offsetHeight = this.$element.offsetHeight;
    this.rect.offsetTop = this.$element.offsetTop;
    this.rect.offsetLeft = this.$element.offsetLeft;

    if (this.verticalScroll) {
      this.setScrollRect(this.verticalScroll.instance);
      this.setScrollStyle(this.verticalScroll.instance);
    }

    if (this.horizontalScroll) {
      this.setScrollRect(this.horizontalScroll.instance);
      this.setScrollStyle(this.horizontalScroll.instance);
    }
  }

  private addScrollbar(orientation: string) {
    const scrollbar: ComponentRef<ScrollbarComponent> = this.appendComponent.appendComponent(
      ScrollbarComponent,
      this.$parent
    );
    scrollbar.instance.orientation = orientation;
    scrollbar.instance.small = true;
    scrollbar.instance.hidden = !this.visible;
    this.setScrollRect(scrollbar.instance);
    this.setScrollStyle(scrollbar.instance);

    scrollbar.instance.positionChange.subscribe(position => {
      if (scrollbar.instance.orientation === 'vertical') {
        this.$element.scrollTop = position;
      } else {
        this.$element.scrollLeft = position * (this.rtl ? -1 : 1);
      }
    });

    scrollbar.instance.element.nativeElement.addEventListener('mouseleave', () => {
      if (!scrollbar.instance.isChanging) {
        scrollbar.instance.hidden = !this.visible;
        scrollbar.instance.small = true;
        scrollbar.instance.detectChanges();
      } else {
        const sub = scrollbar.instance.positionChangeEnded.subscribe(() => {
          scrollbar.instance.hidden = !this.visible;
          scrollbar.instance.small = true;
          scrollbar.instance.detectChanges();
          sub.unsubscribe();
        });
      }
    });

    scrollbar.instance.detectChanges();

    return scrollbar;
  }

  private removeScrollbar(scrollbar: ComponentRef<ScrollbarComponent>) {
    scrollbar && this.appendComponent.removeComponent(scrollbar);
    return undefined;
  }

  private setScrollStyle(scroll: ScrollbarComponent) {
    const position = this.window.getComputedStyle(this.$parent).getPropertyValue('position').toLocaleLowerCase();
    if (position === 'static') {
      this.$parent.style.position = 'relative';
    }

    const element = scroll.element.nativeElement;
    element.style.position = 'absolute';
    element.style.bottom = this.$parent.offsetHeight - this.$element.offsetTop - this.$element.clientHeight + 'px';
    element.style.right = '';
    element.style.left = '';
    element.style.top = '';
    element.style.zIndex = '100';

    if (scroll.orientation === 'vertical') {
      element.style.top = this.$element.offsetTop + 'px';
      if (this.rtl) {
        element.style.left = this.$parent.offsetWidth - this.$element.offsetLeft - this.$element.clientWidth + 'px';
      } else {
        element.style.right = this.$parent.offsetWidth - this.$element.offsetLeft - this.$element.clientWidth + 'px';
      }
    } else {
      element.style.left = this.$element.offsetLeft + 'px';
      element.style.right = this.$parent.offsetWidth - this.$element.offsetLeft - this.$element.clientWidth + 'px';
    }
  }

  private setScrollRect(scroll: ScrollbarComponent) {
    if (scroll.orientation === 'vertical') {
      scroll.offsetSize = this.$element.offsetHeight;
      scroll.scrollSize = this.$element.scrollHeight;
      scroll.position = this.$element.scrollTop;
    } else {
      scroll.offsetSize = this.$element.offsetWidth;
      scroll.scrollSize = this.$element.scrollWidth;
      scroll.position = this.$element.scrollLeft;
      scroll.reverse = this.rtl;
    }

    scroll.ngOnChanges({
      offsetSize: {
        currentValue: scroll.offsetSize,
        previousValue: scroll.offsetSize + 1,
        firstChange: false,
        isFirstChange: () => false
      },
      scrollSize: {
        currentValue: scroll.scrollSize,
        previousValue: scroll.scrollSize + 1,
        firstChange: false,
        isFirstChange: () => false
      }
    });
  }
}
